import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Crown, Star, BadgeCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const TIERS = [
    {
        id: "silver",
        icon: Star,
        color: "text-muted-foreground",
        title: "Silver",
        priceKey: "agent_tier_silver_usd",
        perks: [
            "Verified Agent badge on every listing",
            "Reference checks + listing-quality audit",
            "Search-results boost",
        ],
    },
    {
        id: "gold",
        icon: Crown,
        color: "text-coral",
        title: "Gold",
        priceKey: "agent_tier_gold_usd",
        perks: [
            "Everything in Silver",
            "Dedicated relationship manager",
            "Monthly investor-match digest",
            "Higher Concierge kickback rate",
        ],
    },
];

export default function UpgradeTierDialog({ user, onClose }) {
    const [settings, setSettings] = useState(null);
    const [submitting, setSubmitting] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/platform/settings");
                setSettings(res.data);
            } catch {
                toast.error("Could not load tier prices");
            }
        })();
    }, []);

    const buy = async (tier) => {
        setSubmitting(tier);
        try {
            const res = await api.post("/agent/tier/checkout", {
                tier,
                full_name: user.full_name || "Agent",
                email: user.email,
                origin_url: window.location.origin,
            });
            if (res.data?.checkout_url) {
                window.location.href = res.data.checkout_url;
            } else {
                toast.error("No checkout URL returned");
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
        } finally {
            setSubmitting(null);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="upgrade-tier-dialog" className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground flex items-center gap-2">
                        <Crown className="h-4 w-4 text-coral" /> Upgrade your Verified Agent tier
                    </DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        Annual subscription. Cancel anytime — pro-rata refund within first 30 days.
                    </DialogDescription>
                </DialogHeader>
                {!settings ? (
                    <div className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-coral mx-auto" />
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-3 pt-2" data-testid="tier-options">
                        {TIERS.map((tier) => {
                            const Icon = tier.icon;
                            const price = settings[tier.priceKey];
                            const isCurrent = (user.agent_tier || "bronze") === tier.id;
                            return (
                                <Card key={tier.id} className={`p-5 ${tier.id === "gold" ? "bg-coral/5 dark:bg-coral/10 border-coral/40" : "bg-card"}`}>
                                    <div className="flex items-center gap-2 mb-3">
                                        <Icon className={`h-5 w-5 ${tier.color}`} />
                                        <div className="font-display text-base font-bold text-foreground">{tier.title}</div>
                                        {isCurrent && (
                                            <Badge className="bg-fiji-green text-white text-[10px]">Current</Badge>
                                        )}
                                    </div>
                                    <div className="font-display text-3xl font-bold text-foreground tabular-nums mb-1">
                                        ${price}<span className="text-sm font-normal text-muted-foreground">/yr</span>
                                    </div>
                                    <ul className="space-y-1.5 mt-3 mb-4">
                                        {tier.perks.map((p) => (
                                            <li key={p} className="font-app text-xs text-foreground/85 flex items-start gap-1.5">
                                                <BadgeCheck className="h-3 w-3 text-fiji-green mt-0.5 shrink-0" />
                                                <span>{p}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Button
                                        onClick={() => buy(tier.id)}
                                        disabled={submitting === tier.id || isCurrent}
                                        data-testid={`tier-buy-${tier.id}`}
                                        className={`w-full font-app ${tier.id === "gold" ? "bg-coral hover:bg-coral-deep" : "bg-ocean-deep hover:bg-ocean-deep/90"} text-white`}
                                    >
                                        {submitting === tier.id && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
                                        {isCurrent ? "Current tier" : `Subscribe to ${tier.title}`}
                                    </Button>
                                </Card>
                            );
                        })}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} className="font-app">Maybe later</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
